/**
 * Token đọc được từ JavaScript.
 *
 * Nguồn sự thật lúc chạy là các biến CSS trong `globals.css`. Tệp này chỉ giữ
 * phần Tailwind và biểu đồ cần đọc bằng JS: thang bán kính và vai trò của nó.
 *
 * ## Bán kính: dùng mặc định của antd
 *
 * Thang bo góc đi theo **mặc định của antd** (gốc 6px), không theo thang
 * 12/16/24 của đặc tả — xem
 * [ADR-UI-0006](../../../../docs/adr/ui/0006-bo-goc-theo-mac-dinh-antd.md).
 *
 * Cái bẫy: antd **không** dùng thẳng số gốc cho mọi bậc. Nó suy ra `XS`, `SM`,
 * `LG` bằng `genRadius()` (`antd/es/theme/themes/shared/genRadius.js`). Nếu ta
 * khai thang Tailwind bằng tay thì các bậc cùng tên sẽ lệch antd mà không ai
 * thấy — đã từng xảy ra: `rounded-sm` 4px vs `borderRadiusSM` 6px, `rounded`
 * 8px vs `borderRadius` 12px.
 *
 * Vì vậy các bậc khớp antd ở đây **được sinh ra** từ `ANTD_BORDER_RADIUS` bằng
 * chính thuật toán của antd. Đổi một số ở `ANTD_BORDER_RADIUS` là cả hai phía
 * đi theo. Không bao giờ viết tay lại các bậc này.
 */

/**
 * Bán kính gốc truyền cho `ConfigProvider` của antd.
 *
 * `6` là **mặc định của antd 6**: truyền đúng giá trị antd vẫn tự chọn, nên
 * không có gì để lệch (ADR-UI-0006).
 *
 * Đây là **con số duy nhất** cần sửa khi muốn đổi bo góc toàn hệ.
 */
export const ANTD_BORDER_RADIUS = 12;

/**
 * Bản sao `genRadius()` của antd 6.
 *
 * Chép lại thay vì import vì đây là đường dẫn nội bộ của antd
 * (`antd/es/theme/themes/shared/genRadius`), không nằm trong API công khai nên
 * có thể đổi giữa các bản minor mà không báo. `web/admin/scripts/check-design.mjs`
 * đối chiếu kết quả của hàm này với giá trị antd thật lúc chạy, nên nếu antd đổi
 * thuật toán thì `pnpm test` sẽ gãy chứ không lệch âm thầm.
 */
function genRadius(radiusBase: number) {
  let radiusLG = radiusBase;
  let radiusSM = radiusBase;
  let radiusXS = radiusBase;

  if (radiusBase < 6 && radiusBase >= 5) radiusLG = radiusBase + 1;
  else if (radiusBase < 16 && radiusBase >= 6) radiusLG = radiusBase + 2;
  else if (radiusBase >= 16) radiusLG = 16;

  if (radiusBase < 7 && radiusBase >= 5) radiusSM = 4;
  else if (radiusBase < 8 && radiusBase >= 7) radiusSM = 5;
  else if (radiusBase < 14 && radiusBase >= 8) radiusSM = 6;
  else if (radiusBase < 16 && radiusBase >= 14) radiusSM = 7;
  else if (radiusBase >= 16) radiusSM = 8;

  if (radiusBase < 6 && radiusBase >= 2) radiusXS = 1;
  else if (radiusBase >= 6) radiusXS = 2;

  return { radiusXS, radiusSM, radiusBase, radiusLG };
}

const antd = genRadius(ANTD_BORDER_RADIUS);

/**
 * Thang bán kính của Tailwind, thay toàn bộ thang mặc định.
 *
 * **Mọi** bậc đều sinh từ antd nên luôn khớp component của antd:
 *
 * | Class | Giá trị (gốc 6) | Token antd | Dùng cho |
 * | --- | --- | --- | --- |
 * | `rounded-xs` | 2px | `borderRadiusXS` | chi tiết rất nhỏ |
 * | `rounded-sm` | 4px | `borderRadiusSM` | vòng focus |
 * | `rounded` | 6px | `borderRadius` | nút, ô nhập, select, chip, khối lồng |
 * | `rounded-lg` | 8px | `borderRadiusLG` | thẻ, panel lớp ngoài, hộp thoại, sidebar |
 * | `rounded-full` | 9999px | — | nhãn trạng thái, avatar, chấm, thanh tiến trình |
 *
 * **Cố ý không khai** `md`, `xl`, `2xl`, `3xl`. Thang của antd dừng ở `lg`;
 * để chúng lại như bí danh cùng giá trị sẽ khiến người đọc tin `rounded-2xl`
 * bo nhiều hơn `rounded-lg` trong khi hai class cho cùng một góc. Bỏ hẳn thì
 * Tailwind báo class không tồn tại — đó là phản hồi đúng (ADR-UI-0006).
 */
export const radius = {
  none: '0',
  xs: 'var(--radius-xs)',
  sm: 'var(--radius-sm)',
  DEFAULT: 'var(--radius-base)',
  lg: 'var(--radius-lg)',
  full: '9999px',
} as const;

/**
 * Giá trị mặc định của bốn bậc, ghi vào `globals.css`.
 *
 * Tailwind biên dịch lúc build nên **không thể** đọc lựa chọn lúc chạy. Vì vậy
 * thang ở trên trỏ vào biến CSS, còn giá trị thật do `globals.css` đặt mặc
 * định và `styles/tuy-chinh.ts` ghi đè theo lựa chọn của người dùng — nhờ đó
 * class viết tay và component antd đổi bo góc **cùng lúc**.
 */
export const RADIUS_DEFAULTS = {
  '--radius-xs': `${antd.radiusXS}px`,
  '--radius-sm': `${antd.radiusSM}px`,
  '--radius-base': `${antd.radiusBase}px`,
  '--radius-lg': `${antd.radiusLG}px`,
} as const;

/**
 * Suy ra bốn bậc bán kính từ một gốc, bằng chính thuật toán của antd.
 *
 * Dùng khi người dùng đổi độ bo góc: antd nhận số gốc và tự suy, còn Tailwind
 * đọc kết quả của hàm này qua biến CSS. Hai bên vì vậy không thể lệch.
 */
export function thangBanKinh(goc: number) {
  const r = genRadius(goc);
  return {
    '--radius-xs': `${r.radiusXS}px`,
    '--radius-sm': `${r.radiusSM}px`,
    '--radius-base': `${r.radiusBase}px`,
    '--radius-lg': `${r.radiusLG}px`,
  };
}

/**
 * Bán kính gán theo vai trò.
 *
 * Thang của antd chỉ có hai bậc dùng được cho hộp chứa, nên `nested` và
 * `control` **cùng** 6px: khối lồng trong thẻ không cần bo khác control. Chỉ
 * panel lớp ngoài mới lên 8px.
 *
 * Dùng bảng này để **chọn class** khi viết màn mới; giá trị chạy thật vẫn đi
 * qua Tailwind.
 */
export const radiusRole = {
  /** Ảnh đại diện, nhãn trạng thái, chấm, thanh tiến trình, nút tròn. */
  pill: radius.full,
  /** Nút, ô nhập, select, chip nhỏ. */
  control: radius.DEFAULT,
  /** Khối lồng trong thẻ: lưới số liệu, ô con, skeleton. */
  nested: radius.DEFAULT,
  /** Thẻ và panel lớp ngoài, hộp thoại, PageHeader, thanh điều hướng. */
  card: radius.lg,
} as const;

/** Thang z-index (§5.15). antd tự quản lý lớp của nó. */
export const zIndex = {
  hide: -1,
  base: 0,
  raised: 10,
  dropdown: 20,
  sticky: 30,
  overlay: 40,
  modal: 50,
  popover: 60,
  toast: 70,
  tooltip: 80,
} as const;

/**
 * Màu biểu đồ đọc bằng JS. Đã kiểm với người khiếm sắc (ΔE ≥ 23) (§5.3).
 * Lưu ý thang mức độ **đảo chiều** ở nền tối: nền sáng thì nặng hơn = tối hơn,
 * nền tối thì nặng hơn = sáng hơn, để tương phản tối đa với `#0F172A`.
 */
export const chartTokens = {
  cat1: 'var(--chart-cat-1)',
  cat2: 'var(--chart-cat-2)',
  sevLow: 'var(--chart-sev-low)',
  sevMedium: 'var(--chart-sev-medium)',
  sevHigh: 'var(--chart-sev-high)',
  sevCritical: 'var(--chart-sev-critical)',
} as const;

/**
 * Màu nhấn mặc định của khung chat nhúng (module `tro-ly-ai`, `Widget.primaryColor`).
 *
 * Là **dữ liệu** gửi lên máy chủ rồi đi vào trang web của khách, không phải màu của giao diện này — nên phải
 * là một mã hex thật, không phải biến CSS. Nằm ở đây vì đây là chỗ `check:design` cho phép hex.
 */
export const WIDGET_MAU_MAC_DINH = '#2563EB';
